import { computed, ref } from 'vue';
import { getApiToken } from './api';
import { apiUrl } from './api-url';
import { markNetworkRequestFailed, markNetworkRequestSucceeded, refreshConnectivityAfterResume } from './connectivity';
import type { SalesDocumentType } from '../types/public-invoice';

const STORAGE_KEY = 'eqover-offline-queue-v1';
const SURVEY_IDS_KEY = 'eqover-offline-survey-ids-v1';
const DATABASE_NAME = 'eqover-offline-content';
const DATABASE_VERSION = 1;
const BLOB_STORE = 'evidence-blobs';

export type OfflineQueueKind = 'survey' | 'evidence';

interface OfflineQueueBase {
  key: string;
  kind: OfflineQueueKind;
  documentType: SalesDocumentType;
  folio: string;
  normalizedFolio: string;
  amount: string;
  createdAt: string;
}

export interface OfflineSurveyQueueItem extends OfflineQueueBase {
  kind: 'survey';
  payload: Record<string, unknown>;
}

export interface OfflineEvidenceFile {
  blobId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  evidenceType: 'image' | 'video';
  evidenceContext: 'delivery' | 'unsuitable_space';
}

export interface OfflineEvidenceQueueItem extends OfflineQueueBase {
  kind: 'evidence';
  mode: 'survey' | 'invoice';
  surveyId: string | null;
  files: OfflineEvidenceFile[];
}

export type OfflineQueueItem = OfflineSurveyQueueItem | OfflineEvidenceQueueItem;

export interface QueuedEvidencePreview extends OfflineEvidenceFile {
  blob: Blob;
}

export interface OfflineUploadedEvidence {
  id: string;
  type: 'image' | 'video';
  context: 'delivery' | 'unsuitable_space';
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export const offlineQueueItems = ref<OfflineQueueItem[]>(readStoredQueue());
export const offlineQueueUploading = ref(false);
export const offlineQueueError = ref('');
export const offlineQueueCount = computed(() => offlineQueueItems.value.length);
export const offlineQueuePreviewEnabled = ref(false);
export const offlineQueuePreviewActive = computed(() => offlineQueuePreviewEnabled.value && offlineQueueCount.value === 0);
export const offlineQueueDisplayCount = computed(() => offlineQueueCount.value || (offlineQueuePreviewEnabled.value ? 3 : 0));

export function setOfflineQueuePreview(enabled: boolean): void {
  offlineQueuePreviewEnabled.value = enabled;
}

let started = false;

export function startOfflineQueue(): void {
  if (started) return;
  started = true;
  window.addEventListener('online', handleOnline);
  window.addEventListener('focus', handleOnline);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  if (navigator.onLine && offlineQueueItems.value.length) void retryOfflineQueue();
}

export function stopOfflineQueue(): void {
  if (!started) return;
  started = false;
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('focus', handleOnline);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
}

function handleOnline(): void {
  if (!navigator.onLine) return;
  refreshConnectivityAfterResume();
  void retryOfflineQueue();
}

function handleVisibilityChange(): void {
  if (!document.hidden && navigator.onLine) {
    refreshConnectivityAfterResume();
    void retryOfflineQueue();
  }
}

function scheduleRetry(): void {
  if (!started || !navigator.onLine) return;
  window.setTimeout(() => { void retryOfflineQueue(); }, 0);
}

export function isNetworkUnavailable(cause?: unknown): boolean {
  if (!navigator.onLine) {
    markNetworkRequestFailed(cause);
    return true;
  }
  if (!(cause instanceof Error)) return false;
  const unavailable = cause instanceof TypeError || /failed to fetch|network request failed|networkerror|load failed|internet connection/i.test(cause.message);
  if (unavailable) markNetworkRequestFailed(cause);
  return unavailable;
}

export async function hasOfflineAction(kind: OfflineQueueKind, documentType: SalesDocumentType, folio: string, evidenceContext = ''): Promise<boolean> {
  const key = await queueKey(kind, documentType, folio, evidenceContext);
  return offlineQueueItems.value.some((item) => item.key === key);
}

export async function queueSurveySubmission(input: {
  documentType: SalesDocumentType;
  folio: string;
  amount: string;
  payload: Record<string, unknown>;
}): Promise<void> {
  const normalizedFolio = normalizeOfflineFolio(input.folio, input.documentType);
  const item: OfflineSurveyQueueItem = {
    key: await queueKey('survey', input.documentType, input.folio),
    kind: 'survey',
    documentType: input.documentType,
    folio: input.folio,
    normalizedFolio,
    amount: input.amount,
    payload: input.payload,
    createdAt: new Date().toISOString(),
  };
  replaceQueueItem(item);
  scheduleRetry();
}

export async function queueEvidenceFiles(input: {
  documentType: SalesDocumentType;
  folio: string;
  amount: string;
  mode: 'survey' | 'invoice';
  surveyId: string | null;
  evidenceContext: 'delivery' | 'unsuitable_space';
  files: Array<{ file: File; evidenceType: 'image' | 'video' }>;
}): Promise<OfflineEvidenceFile[]> {
  const key = await queueKey('evidence', input.documentType, input.folio, input.evidenceContext);
  const surveyKey = await queueKey('survey', input.documentType, input.folio);
  const previous = offlineQueueItems.value.find((item): item is OfflineEvidenceQueueItem => item.key === key && item.kind === 'evidence');
  const files = input.files.map(({ file, evidenceType }) => ({
    blobId: createId(),
    filename: file.name || 'evidencia',
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    evidenceType,
    evidenceContext: input.evidenceContext,
  }));
  try {
    await Promise.all(files.map((metadata, index) => putBlob(metadata.blobId, input.files[index]!.file)));
    replaceQueueItem({
      key,
      kind: 'evidence',
      documentType: input.documentType,
      folio: input.folio,
      normalizedFolio: normalizeOfflineFolio(input.folio, input.documentType),
      amount: input.amount,
      mode: input.mode,
      surveyId: input.surveyId || (input.mode === 'survey' ? readSurveyIds()[surveyKey] ?? null : null),
      files,
      createdAt: new Date().toISOString(),
    });
  } catch (cause) {
    await Promise.allSettled(files.map((file) => deleteBlob(file.blobId)));
    throw cause;
  }
  if (previous) await Promise.allSettled(previous.files.map((file) => deleteBlob(file.blobId)));
  scheduleRetry();
  return files;
}

export async function queuedEvidencePreviews(documentType: SalesDocumentType, folio: string, evidenceContext: 'delivery' | 'unsuitable_space' = 'delivery'): Promise<QueuedEvidencePreview[]> {
  const key = await queueKey('evidence', documentType, folio, evidenceContext);
  const item = offlineQueueItems.value.find((entry): entry is OfflineEvidenceQueueItem => entry.key === key && entry.kind === 'evidence');
  if (!item) return [];
  const previews = await Promise.all(item.files.map(async (file) => ({ ...file, blob: await getBlob(file.blobId) })));
  return previews.filter((entry): entry is QueuedEvidencePreview => Boolean(entry.blob));
}

export async function removeQueuedEvidenceFile(documentType: SalesDocumentType, folio: string, blobId: string, evidenceContext: 'delivery' | 'unsuitable_space' = 'delivery'): Promise<void> {
  const key = await queueKey('evidence', documentType, folio, evidenceContext);
  const item = offlineQueueItems.value.find((entry): entry is OfflineEvidenceQueueItem => entry.key === key && entry.kind === 'evidence');
  if (!item) return;
  const files = item.files.filter((file) => file.blobId !== blobId);
  if (files.length) replaceQueueItem({ ...item, files });
  else removeQueueMetadata(key);
  await deleteBlob(blobId);
}

export async function removeOfflineAction(kind: OfflineQueueKind, documentType: SalesDocumentType, folio: string, evidenceContext = ''): Promise<void> {
  const key = await queueKey(kind, documentType, folio, evidenceContext);
  await removeQueueItem(key);
}

export async function completeOnlineSurvey(documentType: SalesDocumentType, folio: string, surveyId: string | null): Promise<void> {
  if (surveyId) {
    storeSurveyId(await queueKey('survey', documentType, folio), surveyId);
    await attachSurveyIdToEvidence(documentType, folio, surveyId);
  }
  await removeOfflineAction('survey', documentType, folio);
}

export async function retryOfflineQueue(): Promise<void> {
  if (offlineQueueUploading.value || !offlineQueueItems.value.length) return;
  if (!navigator.onLine) {
    markNetworkRequestFailed(new TypeError('No hay conexión a internet.'));
    offlineQueueError.value = 'Seguimos sin conexión. Lo volveremos a intentar automáticamente.';
    return;
  }
  offlineQueueUploading.value = true;
  offlineQueueError.value = '';
  try {
    const failures: string[] = [];
    const surveys = offlineQueueItems.value.filter((item): item is OfflineSurveyQueueItem => item.kind === 'survey');
    for (const item of surveys) {
      try {
        const result = await postSurvey(item.payload);
        if (result.id) {
          storeSurveyId(item.key, result.id);
          await attachSurveyIdToEvidence(item.documentType, item.folio, result.id);
        }
        await removeQueueItem(item.key);
        window.dispatchEvent(new CustomEvent('eqover:offline-survey-uploaded', {
          detail: {
            documentType: item.documentType,
            folio: item.folio,
            id: result.id,
            preview: result.preview,
            alreadySubmitted: result.alreadySubmitted,
          },
        }));
      } catch (cause) {
        if (isNetworkUnavailable(cause)) throw cause;
        failures.push((cause as Error).message || `No fue posible subir la encuesta ${item.normalizedFolio}.`);
      }
    }
    const evidences = offlineQueueItems.value.filter((item): item is OfflineEvidenceQueueItem => item.kind === 'evidence');
    for (const item of evidences) {
      try { await uploadEvidenceItem(item); }
      catch (cause) {
        if (isNetworkUnavailable(cause)) throw cause;
        failures.push((cause as Error).message || `No fue posible subir las evidencias ${item.normalizedFolio}.`);
      }
    }
    if (failures.length) {
      offlineQueueError.value = failures.length === 1
        ? failures[0]!
        : `${failures.length} registros no pudieron subirse. ${failures[0]}`;
    }
  } catch (cause) {
    offlineQueueError.value = isNetworkUnavailable(cause)
      ? 'Seguimos sin conexión. Lo volveremos a intentar automáticamente.'
      : (cause as Error).message || 'No fue posible subir la información pendiente.';
  } finally {
    offlineQueueUploading.value = false;
  }
}

async function postSurvey(payload: Record<string, unknown>): Promise<{ id: string | null; preview: boolean; alreadySubmitted: boolean }> {
  const response = await fetch(apiUrl('/api/public/surveys/submissions'), {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const result = await response.json() as { data?: { id: string | null; preview: boolean; alreadySubmitted: boolean }; error?: string };
  markNetworkRequestSucceeded();
  if (!response.ok || !result.data) throw new Error(result.error || 'No fue posible subir una encuesta pendiente.');
  return result.data;
}

async function uploadEvidenceItem(item: OfflineEvidenceQueueItem): Promise<void> {
  if (item.mode === 'survey' && !item.surveyId) throw new Error(`La encuesta ${item.normalizedFolio} debe subirse antes que sus evidencias.`);
  for (const file of [...item.files]) {
    const blob = await getBlob(file.blobId);
    if (!blob) throw new Error(`No encontramos el archivo pendiente “${file.filename}” en este dispositivo.`);
    const endpoint = item.mode === 'invoice'
      ? '/api/public/evidences'
      : `/api/public/surveys/${encodeURIComponent(item.surveyId || '')}/evidences`;
    const response = await fetch(apiUrl(endpoint), {
      method: 'PUT',
      headers: evidenceHeaders(item, file),
      body: blob,
    });
    let result: { data?: OfflineUploadedEvidence; error?: string } = {};
    try { result = await response.json() as { data?: OfflineUploadedEvidence; error?: string }; } catch { /* respuesta vacía */ }
    markNetworkRequestSucceeded();
    if (!response.ok || !result.data) throw new Error(result.error || `No fue posible subir “${file.filename}”.`);
    window.dispatchEvent(new CustomEvent('eqover:offline-evidence-uploaded', {
      detail: { documentType: item.documentType, folio: item.folio, blobId: file.blobId, evidence: result.data },
    }));
    await removeUploadedEvidenceFile(item.key, file.blobId);
  }
}

async function removeUploadedEvidenceFile(key: string, blobId: string): Promise<void> {
  const current = offlineQueueItems.value.find((entry): entry is OfflineEvidenceQueueItem => entry.key === key && entry.kind === 'evidence');
  if (!current) return;
  const files = current.files.filter((file) => file.blobId !== blobId);
  if (files.length) replaceQueueItem({ ...current, files });
  else removeQueueMetadata(key);
  await deleteBlob(blobId);
}

async function attachSurveyIdToEvidence(documentType: SalesDocumentType, folio: string, surveyId: string): Promise<void> {
  const normalizedFolio = normalizeOfflineFolio(folio, documentType);
  const items = offlineQueueItems.value.filter((entry): entry is OfflineEvidenceQueueItem =>
    entry.kind === 'evidence' && entry.documentType === documentType && entry.normalizedFolio === normalizedFolio);
  for (const item of items) if (item.mode === 'survey') replaceQueueItem({ ...item, surveyId });
}

function evidenceHeaders(item: OfflineEvidenceQueueItem, file: OfflineEvidenceFile): Record<string, string> {
  const token = getApiToken();
  return {
    'Content-Type': file.mimeType,
    'X-File-Name': encodeURIComponent(file.filename),
    'X-Survey-Document-Type': item.documentType,
    'X-Survey-Folio': item.folio,
    'X-Survey-Amount': item.amount,
    'X-Evidence-Context': file.evidenceContext ?? 'delivery',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function jsonHeaders(): Record<string, string> {
  const token = getApiToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function replaceQueueItem(item: OfflineQueueItem): void {
  const next = offlineQueueItems.value.filter((entry) => entry.key !== item.key);
  next.push(item);
  writeStoredQueue(next);
  offlineQueueItems.value = next;
  offlineQueueError.value = '';
  publishOfflineQueueChanged();
}

async function removeQueueItem(key: string): Promise<void> {
  const item = offlineQueueItems.value.find((entry) => entry.key === key);
  removeQueueMetadata(key);
  if (item?.kind === 'evidence') await Promise.allSettled(item.files.map((file) => deleteBlob(file.blobId)));
}

function removeQueueMetadata(key: string): void {
  const next = offlineQueueItems.value.filter((entry) => entry.key !== key);
  if (next.length === offlineQueueItems.value.length) return;
  writeStoredQueue(next);
  offlineQueueItems.value = next;
  publishOfflineQueueChanged();
}

function publishOfflineQueueChanged(): void {
  window.dispatchEvent(new CustomEvent('eqover:offline-queue-changed', {
    detail: { pendingCount: offlineQueueItems.value.length },
  }));
}

function readStoredQueue(): OfflineQueueItem[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as unknown;
    return Array.isArray(value) ? value.filter(isStoredQueueItem) : [];
  } catch {
    return [];
  }
}

function writeStoredQueue(items: OfflineQueueItem[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function readSurveyIds(): Record<string, string> {
  try {
    const value = JSON.parse(localStorage.getItem(SURVEY_IDS_KEY) || '{}') as unknown;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  } catch {
    return {};
  }
}

function storeSurveyId(key: string, surveyId: string): void {
  const values = readSurveyIds();
  values[key] = surveyId;
  localStorage.setItem(SURVEY_IDS_KEY, JSON.stringify(values));
}

function isStoredQueueItem(value: unknown): value is OfflineQueueItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<OfflineQueueItem>;
  return typeof item.key === 'string' && (item.kind === 'survey' || item.kind === 'evidence')
    && typeof item.folio === 'string' && typeof item.documentType === 'string';
}

export function normalizeOfflineFolio(value: string, _documentType: SalesDocumentType): string {
  const compact = value.normalize('NFKC').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return compact;
}

async function queueKey(kind: OfflineQueueKind, documentType: SalesDocumentType, folio: string, evidenceContext = ''): Promise<string> {
  const base = `${kind}:${documentType}:${normalizeOfflineFolio(folio, documentType)}`;
  // Keep the original key for surveys and normal delivery evidence so queued
  // records created by older app versions remain discoverable.
  const input = kind === 'evidence' && evidenceContext === 'unsuitable_space'
    ? `${base}:unsuitable_space`
    : base;
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

function createId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(BLOB_STORE)) request.result.createObjectStore(BLOB_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('No fue posible abrir el almacenamiento de archivos.'));
  });
}

async function putBlob(id: string, blob: Blob): Promise<void> {
  const database = await openDatabase();
  await databaseRequest(database, 'readwrite', (store) => store.put(blob, id));
  database.close();
}

async function getBlob(id: string): Promise<Blob | undefined> {
  const database = await openDatabase();
  const blob = await databaseRequest<Blob | undefined>(database, 'readonly', (store) => store.get(id));
  database.close();
  return blob;
}

async function deleteBlob(id: string): Promise<void> {
  const database = await openDatabase();
  await databaseRequest(database, 'readwrite', (store) => store.delete(id));
  database.close();
}

function databaseRequest<T = undefined>(database: IDBDatabase, mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(BLOB_STORE, mode);
    const request = operation(transaction.objectStore(BLOB_STORE));
    transaction.oncomplete = () => resolve(request.result as T);
    transaction.onerror = () => reject(transaction.error || request.error || new Error('Falló el almacenamiento local.'));
    transaction.onabort = () => reject(transaction.error || new Error('Se canceló el almacenamiento local.'));
  });
}
